import {
  Button,
  ButtonContainer,
  Card,
  ContentSection,
  Input,
  Layout,
  PageWrap,
  SectionHeading,
  Spinner,
  TileSelect,
  TileSelectGroup
} from '@vwfs-bronson/bronson-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dealer } from '../../../shared/types';
import { FieldError } from '../../components/FieldError/FieldError';
import { TrackableComponent } from '../../components/TrackableComponent/TrackableComponent';
import { LoadMoreButton } from './components/LoadMoreButton';

interface OwnProps {
  prevCall: () => void;
  continueWithDealer: (dealer: Dealer) => void;
  onContinue: () => void;
  onSelectDealer: (dealer: Dealer) => void;
  onFindDealer: (search: string) => void;
  onSearchInputChange: () => void;
  dealers: Dealer[] | undefined;
  preselectedDealer: Dealer | undefined;
  selectedDealer: Dealer | undefined;
  showSelectionError: boolean;
  isLoading?: boolean;
  loadMore: () => void;
  visibleCount: number;
  showMoreButtonVisible: boolean;
  initialSearchTerm: string;
  currentSearchWasSubmitted: boolean;
  setCurrentSearchWasSubmitted: (value: boolean) => void;
  isEditMode?: boolean;
}

export const DealerSelectionComponent: React.FC<OwnProps> = ({
  prevCall,
  continueWithDealer,
  onContinue,
  onSelectDealer,
  onFindDealer,
  onSearchInputChange,
  dealers,
  preselectedDealer,
  selectedDealer,
  showSelectionError,
  isLoading = false,
  loadMore,
  visibleCount,
  showMoreButtonVisible,
  initialSearchTerm,
  currentSearchWasSubmitted,
  setCurrentSearchWasSubmitted,
  isEditMode
}) => {
  const { t } = useTranslation(['dealer-search', 'shared', 'match-maker']);

  const showNoResultsText = currentSearchWasSubmitted && !dealers?.length;
  const visibleDealers = dealers?.slice(0, visibleCount) ?? [];

  const isDealerSelected = (dealer: Dealer) => selectedDealer?.id === dealer.id;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    setCurrentSearchWasSubmitted(true);
    event.preventDefault();
    const form = event.currentTarget;
    const searchTermInput = form.elements.namedItem('searchTerm') as HTMLInputElement | null;
    const searchTerm = searchTermInput?.value?.trim() ?? '';

    if (searchTerm !== '') {
      onFindDealer(searchTerm);
    }
  };

  return (
    <ContentSection data-component-id="dealer-search" className="u-pt-none">
      <PageWrap>
        {preselectedDealer && (
          <ContentSection.ComponentWrapper>
            <>
              <SectionHeading level={2} center>
                {t('pre-selected-dealer-title')}
              </SectionHeading>

              <Card
                titleLevel={3}
                className="u-text-wrap"
                title={preselectedDealer.name}
                footer
                style={{ textAlign: 'center' }}
                buttons={[
                  <TrackableComponent
                    trackingComponent="button"
                    trackingFunction="continueWithDealerButton"
                    key={preselectedDealer.id}
                  >
                    <Button onClick={() => continueWithDealer(preselectedDealer)}>
                      {t('results.continue-button')}
                    </Button>
                  </TrackableComponent>
                ]}
              >
                <address>
                  {preselectedDealer.street} {preselectedDealer.houseNumber}
                  <br></br>
                  {`${preselectedDealer.zip} ${preselectedDealer.city}`}
                </address>
              </Card>
            </>
          </ContentSection.ComponentWrapper>
        )}

        {preselectedDealer && (
          <ContentSection.ComponentWrapper>
            <SectionHeading level={3} center>
              {t('oder')}
            </SectionHeading>
          </ContentSection.ComponentWrapper>
        )}

        <ContentSection.ComponentWrapper>
          <SectionHeading level={2} subtitle={t('subtitle')} center>
            {t('title')}
          </SectionHeading>

          <form role="search" onSubmit={handleSubmit}>
            <Layout center middle>
              <Layout.Item default="3/12" xs="1/1" m="6/12" l="5/12">
                <TrackableComponent
                  trackingComponent="inputbox"
                  trackingFunction="dealerSearchInput"
                >
                  <Input
                    id="searchTerm"
                    defaultValue={initialSearchTerm}
                    placeholder={t('input-placeholder')}
                    onChange={() => {
                      onSearchInputChange();
                    }}
                    aria-label={t('input-placeholder')}
                    aria-required={true}
                  />
                </TrackableComponent>
              </Layout.Item>
              <Layout.Item default="1/12" xs="1/1" m="4/12" l="3/12">
                <TrackableComponent
                  trackingComponent="button"
                  trackingFunction="buttonSearchDealer"
                >
                  <Button role="search" type="submit" full>
                    {t('search-button')}
                  </Button>
                </TrackableComponent>
              </Layout.Item>
            </Layout>

            {showNoResultsText ? (
              <Layout>
                <Layout.Item>
                  <FieldError id="noDealerResultWarning" errorMessage={t('dealer-search-404')} />
                </Layout.Item>
              </Layout>
            ) : null}
          </form>
        </ContentSection.ComponentWrapper>
        <ContentSection.ComponentWrapper>
          {isLoading ? (
            <Spinner fullPage />
          ) : visibleDealers.length > 0 ? (
            <>
              <SectionHeading level={2} center={true}>
                {t('results.header')}
              </SectionHeading>
              <TileSelectGroup
                title={{ text: '' }}
                layoutItemClassName="u-4/12 u-6/12@s u-12/12@xs"
                testId="dealer-selection-results"
                data-testid="dealer-selection-results"
              >
                {visibleDealers.map((dealer) => (
                  <TrackableComponent
                    key={`${dealer.id}-wrapper`}
                    trackingComponent="radiobutton"
                    trackingFunction="selectDealerResult"
                  >
                    <TileSelect
                      className="u-font-size-fs0"
                      id={`dealer-selection-${dealer.id}`}
                      radioGroupName="dealer-selection-results"
                      inputType="radio"
                      value={dealer.id}
                      checked={isDealerSelected(dealer)}
                      onChange={() => onSelectDealer(dealer)}
                      title={dealer.name}
                    >
                      <address>
                        {dealer.street} {dealer.houseNumber}
                        <br></br>
                        {`${dealer.zip} ${dealer.city}`}
                      </address>
                    </TileSelect>
                  </TrackableComponent>
                ))}
              </TileSelectGroup>

              <LoadMoreButton
                t={t}
                showMoreButtonVisible={showMoreButtonVisible}
                loadMore={loadMore}
              />
            </>
          ) : null}

          {showSelectionError ? (
            <Layout>
              <Layout.Item>
                <FieldError id="dealerSelectionRequired" errorMessage={t('validation.required')} />
              </Layout.Item>
            </Layout>
          ) : null}

          <ButtonContainer nav className="u-mt-large">
            {isEditMode && (
              <Button
                onClick={() => continueWithDealer(selectedDealer ?? (preselectedDealer as Dealer))}
              >
                {t('match-maker:question-pages.save-continue-button')}
              </Button>
            )}
            {!isEditMode && (
              <>
                <TrackableComponent
                  trackingComponent="button"
                  trackingFunction="buttonBackToDealerAppointmentPage"
                >
                  <Button
                    secondary
                    type="button"
                    onClick={prevCall}
                    className="u-1/6 u-1/3@m u-1/1@xs o-button-container__button"
                  >
                    {t('navigation.back-button')}
                  </Button>
                </TrackableComponent>
                <TrackableComponent trackingComponent="button" trackingFunction="buttonNext">
                  <Button
                    type="button"
                    onClick={onContinue}
                    disabled={isLoading}
                    className="u-1/6 u-1/3@m u-1/1@xs o-button-container__button"
                  >
                    {t('navigation.next-button')}
                  </Button>
                </TrackableComponent>
              </>
            )}
          </ButtonContainer>
        </ContentSection.ComponentWrapper>
      </PageWrap>
    </ContentSection>
  );
};
